import type { PendingPublish } from './pendingPublish';
import { ConfigData, PROJECT_PULL_CONFIG_KEYS } from 'serviceWorker/api/types';
import { ServiceData } from 'serviceWorker/db/service';
import type { DataOwner } from 'serviceWorker/db/dataOwner';
import { debugLog } from 'shared/logger/debugLog';
import { isNonEmptyArray } from 'shared/utils/array';
import { LogLevel } from 'shared/types/logger';
import { broadcastPullFailed, type PullingContract } from 'serviceWorker/helpers/broadcastPullError';
import { createRunAfterSignal } from 'serviceWorker/events/runAfterSignal';
import { swSignals } from 'serviceWorker/events/swSignals';
import { resolveAuthenticatedConfig } from '../config';
import { createConfigRetry, logWaitsForConfig } from 'serviceWorker/helpers/createConfigRetry';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { logGroupedEntityFailure } from 'serviceWorker/helpers/entityFailureLog';
import { getPullDiagnostics } from 'serviceWorker/helpers/pullDiagnostics';
import { createPullGuard } from './createPullGuard';
import { createPullFailureCooldown } from './pullFailureCooldown';
import { runPullInBackground } from './runPullInBackground';
import { PullStep, pullStepMarker } from './pullProgress';
import { FailedPull, Pull } from './types';
import { Services } from 'shared/domain/messages/message';
import { captureCollectionScope, ownerOfService } from 'serviceWorker/helpers/dataScope';

/** A pull running longer than this is logged as possibly hung when a new one is skipped. */
const LONG_PULL_MS = 30_000;

interface MakePullHandlerProps {
  entityRepository: { clear: () => Promise<void> };
  entityService: {
    /** Whom the collection belongs to, as its table declares. */
    owner?: DataOwner;
    get: () => Promise<undefined | ServiceData>;
    reset: () => Promise<void>;
    add: (data: Partial<ServiceData>) => Promise<void>;
    stopDownloading: () => Promise<void>;
  };
  resync: Pull;
  /** Dropped when the pull is aborted. */
  pendingCollection: PendingPublish;
  customRequirements?: (keyof ConfigData)[];
  withoutSyncKey?: boolean;
  /**
   * Clears the stored items and the download state before a pull (default).
   * Off when the pull replaces the stored items in one go, so they stay
   * readable while it runs.
   */
  clearBeforePull?: boolean;
  /** The entity's channel: its pull steps and its running pull are kept by its key. */
  channelContract: PullingContract;
  entityName: Services;
}

export interface PullOptions {
  /**
   * Pulls only a collection that is not downloaded, also for an entity whose
   * syncs download it again (no sync key): one guard serves both modes.
   */
  onlyWhenMissing?: boolean;
}

/** Starts the pull when it should run; `isRunning` tells whether one is in progress. */
export type PullHandler = ((options?: PullOptions) => Promise<void>) & { isRunning: () => boolean };

interface PullNeedOptions {
  withoutSyncKey?: boolean;
  onlyWhenMissing?: boolean;
}

/**
 * Whether the entity should be downloaded now: never while it is marked as
 * downloading. Without a sync key a pull downloads it again, unless only a
 * missing collection is wanted; a collection marked to be downloaded again
 * (`shouldReset`) is pulled like a missing one.
 */
function isPullNeeded(
  status: { isDownloading?: boolean; isDownloaded?: boolean; shouldReset?: boolean } | undefined,
  { withoutSyncKey, onlyWhenMissing }: PullNeedOptions
): boolean {
  if (status?.isDownloading) {
    return false;
  }
  if (withoutSyncKey && !onlyWhenMissing) {
    return true;
  }
  return !status?.isDownloaded || Boolean(status?.shouldReset);
}

export function makePullHandler({
  entityRepository: storedEntities,
  entityService: storedService,
  resync,
  pendingCollection,
  customRequirements,
  withoutSyncKey,
  clearBeforePull = true,
  channelContract,
  entityName,
}: MakePullHandlerProps): PullHandler {
  const markStep = pullStepMarker(channelContract);
  const backgroundPull = { entityName, contract: channelContract };
  const pullGuard = createPullGuard(channelContract, { owner: ownerOfService(storedService) });
  const failureCooldown = createPullFailureCooldown();
  let pullStartedAt = 0;
  let optionsWaitingForConfig: PullOptions = {};

  /**
   * Pulls again once the config is there (the worker had none, or not all the
   * pull needs); calls while waiting share that one run. It pulls only a
   * missing collection when every waiting call asked for that; one full pull
   * serves the others too.
   */
  const pullWaitingForConfig = (): void =>
    void runPullInBackground(backgroundPull, () => handler(optionsWaitingForConfig));
  const configRetry = createConfigRetry(`pull:${crypto.randomUUID()}`, pullWaitingForConfig);
  const afterConfigApplied = createRunAfterSignal(swSignals.configApplied, pullWaitingForConfig);
  const rememberOptionsWaitingForConfig = (options: PullOptions, isWaiting: boolean): void => {
    optionsWaitingForConfig = isWaiting
      ? { onlyWhenMissing: Boolean(optionsWaitingForConfig.onlyWhenMissing && options.onlyWhenMissing) }
      : options;
  };

  const handler = async (options: PullOptions = {}): Promise<void> => {
    // Captured before the config is read: a pull decided under outdated data writes nothing.
    const scope = captureCollectionScope(storedService, undefined, entityName);
    const entityService = {
      get: scope.guard(storedService.get),
      reset: scope.guard(storedService.reset),
      add: scope.guard(storedService.add),
      stopDownloading: scope.guard(storedService.stopDownloading),
    };
    const entityRepository = { clear: scope.guard(storedEntities.clear) };
    markStep(PullStep.readingConfig);
    const { config: setup, reason } = await resolveAuthenticatedConfig();
    if (!setup) {
      markStep(PullStep.configUnavailable);
      // Readers wait for the config (their wait does not give up while it is parked), so no failure is broadcast.
      rememberOptionsWaitingForConfig(options, configRetry.isWaiting());
      logWaitsForConfig(entityName, reason, configRetry.wait(reason));
      return;
    }

    configRetry.reset();

    markStep(PullStep.readingState);
    let status = await entityService.get();
    if (!(await scope.isCurrent())) {
      markStep(PullStep.scopeOutdated);
      return;
    }
    const required = customRequirements ?? PROJECT_PULL_CONFIG_KEYS;
    const missingConfigKeys = required.filter((key) => !setup[key]);

    if (isNonEmptyArray(missingConfigKeys)) {
      markStep(PullStep.waitingForConfig);
      // Not a failure: the page has not configured the worker yet. The config applied later starts the pull.
      logGroupedEntityFailure(
        'Pull waits for the config',
        { entityName, setup: { missingConfigKeys } },
        { fingerprint: ['sw-pull-waits', 'config-missing'] },
        LogLevel.INFO
      );
      rememberOptionsWaitingForConfig(options, afterConfigApplied.isWaiting());
      afterConfigApplied.ask();
      return;
    }

    // Persisted isDownloading without an in-memory pull is leftover from a
    // dead attempt (SW restart, project change, thrown setup). Clear it so
    // the next sync can run. Never clear while this session still has a pull.
    if (status?.isDownloading && !pullGuard.isPulling) {
      debugLog(
        'Resetting stale isDownloading flag',
        status,
        'entityName:' + (resync?.name || 'unknown')
      );
      await entityService.stopDownloading();
      status = {
        ...status,
        isDownloading: false,
      };
    }

    const shouldExecute = isPullNeeded(status, { withoutSyncKey, onlyWhenMissing: options.onlyWhenMissing });
    withoutSyncKey && debugLog('Dispatching pull?, sync key is disabled', shouldExecute, status);

    if (!shouldExecute) {
      markStep(PullStep.notNeeded);
      if (pullGuard.isPulling && Date.now() - pullStartedAt > LONG_PULL_MS) {
        // The running pull broadcasts when it ends. If it hangs (e.g. a fetch
        // stuck after the laptop slept) listeners wait – this log shows it.
        swLog(
          `[${entityName}] Pull skipped: previous pull still running`,
          LogLevel.INFO,
          null,
          {
            entityName,
            setup: {
              ...getPullDiagnostics(),
              pullRunningMs: Date.now() - pullStartedAt,
            },
          }
        );
      }
      return;
    }

    // `execute` skips it too; the step tells why this pull did nothing.
    if (pullGuard.isPulling) {
      markStep(PullStep.alreadyRunning);
      return;
    }

    // Readers stop waiting as after the failure itself; nothing is cleared, asked or logged again.
    const recentFailure = failureCooldown.failureToRepeat(scope.projectId);
    if (recentFailure) {
      markStep(PullStep.failedRecently);
      await broadcastPullFailed({ channelContract, entityName, error: recentFailure.error, projectId: scope.projectId });
      return;
    }
    await pullGuard.execute(async (abortController) => {
      markStep(PullStep.started);
      pullStartedAt = Date.now();
      // A download that starts always ends with the collection (a skipped one publishes nothing).
      pendingCollection.request();
      try {
        if (clearBeforePull) {
          await entityService.reset();
        }
        const initialState = withoutSyncKey
          ? {
              total: undefined,
              isDownloading: true,
              isDownloaded: false,
            }
          : {
              total: undefined,
              isDownloading: true,
              isDownloaded: false,
              syncKey: undefined,
            };
        await entityService.add(initialState);
        if (clearBeforePull) {
          await entityRepository.clear();
        }
        if (!(await scope.isCurrent())) {
          markStep(PullStep.scopeOutdated);
          return;
        }
        // The pull writes in this scope: the config it uses was read in it.
        const promise = resync(setup, abortController, scope);
        if (!promise) {
          markStep(PullStep.fetchSkipped);
          await entityService.stopDownloading();
          return;
        }
        let failedPull: FailedPull | void;
        try {
          failedPull = await promise;
        } catch (error) {
          // Only a download that failed waits before it is asked again: a local setup error may be
          // retried at once, and an aborted one (logout, project switch) failed at nothing.
          if (!abortController.signal.aborted) {
            failureCooldown.remember(scope.projectId, error);
          }
          throw error;
        }
        if (failedPull) {
          // The pull already reset its data and told the failure; it only must not start again at once.
          failureCooldown.remember(scope.projectId, failedPull.error);
          markStep(PullStep.failed);
          return;
        }
        markStep(PullStep.ended);
        failureCooldown.forget();
      } catch (error) {
        markStep(PullStep.failed);
        await entityService.stopDownloading();
        throw error;
      }
    }, pendingCollection.cancel);
  };

  return Object.assign(handler, { isRunning: () => pullGuard.isPulling });
}
